import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

/**
 * TransformationsPanel — Phase 2.5
 *
 * Renders below the Apply to Data success banner in FieldMapping.js,
 * and also on tab return whenever mapped_working.csv has been applied
 * (detected by FieldMapping checking GET /api/transformations/summary on mount).
 *
 * Mount strategy:
 *   - Calls GET /api/transformations/summary first.
 *   - If detected_at is non-null (prior detection exists), uses the summary
 *     directly — preserves applied/skipped statuses without re-scanning.
 *   - If detected_at is null (first time this session), falls through to
 *     GET /api/transformations/detect to run the initial scan.
 *
 * After Apply or Skip: calls GET /api/transformations/summary (not detect),
 * so the applied status written by the backend is read back intact.
 * Calling detect after apply would re-scan mapped_working.csv, find no more
 * empty Titles (they were just filled), and overwrite the applied status with
 * an empty issues list — losing the record of what was done.
 */
export default function TransformationsPanel() {
  const [data, setData]       = useState(null);   // transformations.json structure
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);   // issue_id currently being actioned

  // Load the persisted summary. Falls through to detect only when no prior
  // detection exists (detected_at is null — first time the panel mounts for
  // a given Apply to Data session).
  const loadSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch(`/transformations/summary`)
      .then(r => r.json())
      .then(json => {
        if (json.error) throw new Error(json.message || String(json.error));
        if (json.detected_at) {
          // Prior detection exists — use it directly; no re-scan needed.
          setData(json);
          setLoading(false);
        } else {
          // No prior detection in this session — run the initial scan now.
          return apiFetch(`/transformations/detect`)
            .then(r => r.json())
            .then(json2 => {
              if (json2.error) throw new Error(json2.message || String(json2.error));
              setData(json2);
              setLoading(false);
            });
        }
      })
      .catch(err => {
        setError(err.message || 'Could not load transformation data.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleApply = async (issueId, params = {}) => {
    setBusy(issueId);
    try {
      const res = await apiFetch(`/transformations/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issue_id: issueId, params }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.message || String(json.error));
    } catch (err) {
      setError(err.message || 'Apply failed. Please try again.');
    }
    setBusy(null);
    // Use summary (not detect) so the backend's applied status is read back
    // without re-scanning mapped_working.csv (which would wipe the applied state).
    loadSummary();
  };

  const handleSkip = async (issueId) => {
    setBusy(issueId);
    try {
      const res = await apiFetch(`/transformations/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issue_id: issueId }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.message || String(json.error));
    } catch (err) {
      setError(err.message || 'Skip failed. Please try again.');
    }
    setBusy(null);
    loadSummary();
  };

  // ── Loading ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500">
        Checking for transformation issues…
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        {error}
      </div>
    );
  }

  const issues = data?.issues ?? [];
  const pendingIssues = issues.filter(iss => iss.status === 'pending');
  const resolvedIssues = issues.filter(iss => iss.status !== 'pending');

  const titleIssues     = issues.filter(iss => iss.type === 'title_derivation');
  const dateIssues      = issues.filter(iss => iss.type === 'date_normalisation');
  const valueIssues     = issues.filter(iss => iss.type === 'value_mapping');
  const tagIssues       = issues.filter(iss => iss.type === 'tag_normalisation');
  const separatorIssues = issues.filter(iss => iss.type === 'separator_normalisation');

  // ── No issues found ──────────────────────────────────────────
  if (issues.length === 0) {
    return (
      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
        ✓ No transformation issues detected — your mapped data looks clean.
      </div>
    );
  }

  // ── Issues panel ─────────────────────────────────────────────
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Data Transformations
            {pendingIssues.length > 0 && (
              <span className="ml-2 inline-block bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded">
                {pendingIssues.length} pending
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Review each issue below. Applied fixes update your mapped data immediately.
            Skipped issues will surface as validation warnings at export.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {titleIssues.length > 0 && (
          <IssueSection label="Titles" issues={titleIssues} busy={busy} onApply={handleApply} onSkip={handleSkip} />
        )}
        {dateIssues.length > 0 && (
          <IssueSection label="Dates" issues={dateIssues} busy={busy} onApply={handleApply} onSkip={handleSkip} />
        )}
        {valueIssues.length > 0 && (
          <IssueSection label="Controlled values" issues={valueIssues} busy={busy} onApply={handleApply} onSkip={handleSkip} />
        )}
        {tagIssues.length > 0 && (
          <IssueSection label="Tags" issues={tagIssues} busy={busy} onApply={handleApply} onSkip={handleSkip} />
        )}
        {separatorIssues.length > 0 && (
          <IssueSection label="Separators" issues={separatorIssues} busy={busy} onApply={handleApply} onSkip={handleSkip} />
        )}
      </div>

      {resolvedIssues.length > 0 && pendingIssues.length === 0 && (
        <div className="mt-3 text-xs text-slate-500 text-right">
          All issues resolved — your data is ready for rationalization.
        </div>
      )}
    </div>
  );
}


// ── IssueSection ─────────────────────────────────────────────────

function IssueSection({ label, issues, busy, onApply, onSkip }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
        {label}
      </div>
      <div className="space-y-3">
        {issues.map(issue => (
          <IssueCard
            key={issue.id}
            issue={issue}
            isBusy={busy === issue.id}
            onApply={(params) => onApply(issue.id, params)}
            onSkip={() => onSkip(issue.id)}
          />
        ))}
      </div>
    </div>
  );
}


// ── IssueCard ────────────────────────────────────────────────────

function IssueCard({ issue, isBusy, onApply, onSkip }) {
  const isApplied = issue.status === 'applied';
  const isSkipped = issue.status === 'skipped';
  const isPending = issue.status === 'pending';

  // Tag normalisation — local state for consultant-configurable options
  const isTagNorm = issue.type === 'tag_normalisation';
  const isValueMapping = issue.type === 'value_mapping';
  const [exclusions, setExclusions] = useState('');
  const [keepFullPath, setKeepFullPath] = useState(true);
  const [valueMappings, setValueMappings] = useState(issue.suggested_mappings || {});

  const handleApplyClick = () => {
    if (isValueMapping) {
      onApply({ mappings: valueMappings });
    } else if (isTagNorm) {
      const exclusionList = exclusions
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      onApply({ exclusions: exclusionList, keep_full_path: keepFullPath });
    } else {
      onApply({});
    }
  };

  let borderColor = 'border-amber-200';
  let bgColor     = 'bg-amber-50';
  if (isApplied) { borderColor = 'border-green-200'; bgColor = 'bg-green-50'; }
  if (isSkipped) { borderColor = 'border-slate-200'; bgColor = 'bg-slate-50'; }

  return (
    <div className={`border ${borderColor} ${bgColor} rounded-lg p-4`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              {issue.column}
            </span>
            {isApplied && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                ✓ Applied{issue.rows_changed !== undefined ? ` — ${issue.rows_changed.toLocaleString()} rows updated` : ''}
              </span>
            )}
            {isSkipped && (
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                Skipped
              </span>
            )}
          </div>
          <p className="text-sm text-slate-800 mt-1">{issue.problem}</p>
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-medium">Proposed fix:</span> {issue.fix}
          </p>
        </div>

        {/* Actions — only shown for pending issues */}
        {isPending && !issue.unavailable && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onSkip}
              disabled={isBusy}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isBusy ? '…' : 'Skip'}
            </button>
            <button
              onClick={handleApplyClick}
              disabled={isBusy}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isBusy ? 'Applying…' : `Apply to ${issue.affected_count.toLocaleString()} rows`}
            </button>
          </div>
        )}
      </div>

      {/* Tag normalisation — pattern label + consultant controls */}
      {isTagNorm && isPending && (
        <div className="mt-3 space-y-3">
          {issue.pattern_label && (
            <p className="text-xs text-slate-500">
              <span className="font-medium">Detected pattern:</span> {issue.pattern_label}
            </p>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Segments to exclude <span className="font-normal text-slate-400">(optional — comma-separated)</span>
            </label>
            <input
              type="text"
              value={exclusions}
              onChange={e => setExclusions(e.target.value)}
              placeholder='e.g. content-type, design-center'
              className="w-full text-xs border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              Path segments or prefixes listed here will be removed from the output tags.
            </p>
          </div>
          {issue.pattern === 'HIERARCHY' && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={keepFullPath}
                onChange={e => setKeepFullPath(e.target.checked)}
                className="rounded"
              />
              Keep all path segments (uncheck to keep leaf value only)
            </label>
          )}
        </div>
      )}

      {isValueMapping && isPending && (
        <div className="mt-3 space-y-2">
          {Object.entries(issue.value_counts || {}).map(([sourceValue, count]) => (
            <div key={sourceValue} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(10rem,0.7fr)] items-center gap-3 text-xs">
              <div className="truncate text-slate-700" title={sourceValue}>
                {sourceValue} <span className="text-slate-400">({Number(count).toLocaleString()} rows)</span>
              </div>
              <span className="text-slate-400">→</span>
              <select
                value={valueMappings[sourceValue] || ''}
                onChange={event => setValueMappings(current => ({ ...current, [sourceValue]: event.target.value }))}
                className="border border-slate-300 rounded-lg px-2 py-1.5 bg-white text-slate-700"
              >
                <option value="">Choose…</option>
                {(issue.allowed_values || []).map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
          ))}
          <p className="text-xs text-slate-400">
            Review the suggestions before applying. Every source value must have a destination.
          </p>
        </div>
      )}

      {/* Before / After examples — hidden once applied */}
      {!isApplied && (
        <>
          {/* Single example (title derivation) */}
          {issue.example_before && issue.example_after && (
            <div className="mt-3 flex items-center gap-3 text-xs font-mono">
              <span className="text-slate-400 w-6">e.g.</span>
              <span className="bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-700">
                {issue.example_before}
              </span>
              <span className="text-slate-400">→</span>
              <span className="bg-white border border-green-200 rounded px-2 py-0.5 text-green-700">
                {issue.example_after}
              </span>
            </div>
          )}
          {/* Multiple examples (date normalisation) */}
          {issue.examples && issue.examples.length > 0 && (
            <div className="mt-3 space-y-1">
              {issue.examples.map((ex, i) => (
                <div key={i} className="flex items-center gap-3 text-xs font-mono">
                  <span className={`w-6 ${i === 0 ? 'text-slate-400' : 'invisible'}`}>e.g.</span>
                  <span className="bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-700">
                    {ex.before}
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className="bg-white border border-green-200 rounded px-2 py-0.5 text-green-700">
                    {ex.after}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
